'use client';

import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import CityNotServiceable from './city-not-serviceable';
import NewStoreSession from './session/new-store-session';
import QuickSearch from './session/quick-search';
import SoloMealSection from './session/solo-meal-section';
import NewStoreBanner from './session/store-banner';
import NewStoreCategory from './session/store-category';
import NewStoreDeal from './session/store-deal';
import StoreQuickFilter from './session/store-quick-filter';
import SimpleStoreList from './simple-store-list';

const AmoriContainer = dynamic(
  () => import('@/app/[locale]/amori/amori-container'),
  { ssr: false }
);
import { useGetCampaignBanners } from '@/api/use-get-campaign-banners';
import DesktopLayout from '@/components/desktop-layout';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useCityLocation } from '@/hooks/use-city-location';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import type { UrlCityStatus } from '@/lib/server-location-utils';

interface CityPageClientWrapperProps {
  citySlug: string;
  /** city_id của SLUG trên URL, đã resolve ở SSR. Nguồn duy nhất để query. */
  cityId: string;
  cityStatus?: UrlCityStatus;
  // Server-provided data for initial render (SSR / hydration)
  initialData?: {
    stores: any[];
    categories?: any[]; // Categories for NewStoreCategory section
  };
}

export default function CityPageClientWrapper({
  citySlug: initialCitySlug,
  cityId: urlCityId,
  cityStatus = 'serviceable',
  initialData,
}: CityPageClientWrapperProps) {
  const isWebinapp = useDetectWebInApp();
  const isMobile = useIsMobile();
  const params = useParams();
  const [isAmoriOpen, setIsAmoriOpen] = useState(false);

  // In webinapp mode, get citySlug from URL params for reactive updates
  // In native web mode, use initialCitySlug from server props
  const citySlug = useMemo(() => {
    if (isWebinapp && params.city) {
      return params.city as string;
    }
    return initialCitySlug;
  }, [isWebinapp, params.city, initialCitySlug]);

  const { isNavigating } = useStoreCitySheet();

  // Toạ độ dùng được cho CITY ĐANG DUYỆT (rỗng nếu địa chỉ thuộc city khác).
  const cityLocation = useCityLocation();

  // URL quyết định city đang duyệt: city_id lấy từ slug (SSR resolve), KHÔNG lấy
  // từ city của địa chỉ. Nhờ vậy mở thẳng link một city khác vẫn xem được nội
  // dung, không bị ép nhập địa chỉ.
  //
  // `isNavigating` bật khi user vừa đổi địa chỉ và app đang điều hướng sang city
  // mới. Trong cửa sổ đó URL vẫn là city CŨ — bắn query lúc này là gọi nhầm
  // city_id cũ, đúng bug đã phải revert hồi 20/07.
  const cityId = isNavigating ? '' : urlCityId;

  // AF-062 — 1 call lấy cả 2 type (BE hỗ trợ array param), rồi tách theo type.
  // Hero ← FEATURED_BANNER; dải Super Deal ← SUPER_DEAL. Refetch khi city đổi.
  const { data: banners = [], isLoading: bannersLoading } =
    useGetCampaignBanners(
      {
        city_id: cityId,
        screen: 'home',
        type: ['FEATURED_BANNER', 'SUPER_DEAL'],
      },
      { enabled: !!cityId }
    );
  const featuredBanners = useMemo(
    () => banners.filter((b) => b.type === 'FEATURED_BANNER'),
    [banners]
  );
  const superDeals = useMemo(
    () => banners.filter((b) => b.type === 'SUPER_DEAL'),
    [banners]
  );

  const mainContent = (
    <>
      {/* Categories Section */}
      <NewStoreCategory initialData={initialData?.categories} />

      {/* Quick Search Filters */}
      <QuickSearch citySlug={citySlug} />

      <NewStoreBanner
        banners={featuredBanners}
        superDeals={superDeals}
        isLoading={bannersLoading}
      />

      {/* SoloMeal BẮT BUỘC lat/lng (use-get-solomeal-item) và toạ độ phải thuộc
          đúng city đang duyệt. Thiếu thì ẨN HẲN section, không chèn dải CTA:
          trang chủ vẫn duyệt được đầy đủ bằng city_id nên không có lý do gì
          chắn một khối nhắc nhập địa chỉ vào giữa luồng xem quán.
          Trước đây chỗ này truyền lat=0/lng=0 — tức gọi API với một điểm giữa
          Vịnh Guinea. */}
      {cityLocation.hasLocation ? (
        <SoloMealSection lat={cityLocation.lat!} lng={cityLocation.lng!} />
      ) : null}

      <NewStoreDeal
        cityId={cityId}
        superDeals={superDeals}
        superDealsLoading={bannersLoading}
      />

      {/* New Store Section */}
      <NewStoreSession cityId={cityId} />

      {/* Store List Section */}
      <div className="pb-8 pt-0 w-full">
        <StoreQuickFilter citySlug={citySlug} />
        <SimpleStoreList
          data={initialData?.stores ?? []}
          payload={{ city_id: cityId }}
          processedDeals={[]}
        />
      </div>
    </>
  );

  // List city lấy được nhưng không có slug này → khu vực chưa phục vụ.
  // `unavailable` (không hỏi được list) KHÔNG rơi vào đây: lúc đó vẫn render
  // bình thường, thà thiếu dữ liệu còn hơn báo sai là chưa phục vụ.
  if (cityStatus === 'not_serviceable') {
    return <CityNotServiceable citySlug={citySlug} />;
  }

  return (
    <>
      {/* Mobile: ScrollArea with fullscreen Amori */}
      {isMobile ? (
        <>
          <ScrollArea className="mt-[calc(var(--main-layout-header-height,_0px)_*-1)] bg-white [&>div>div]:!block">
            {mainContent}
          </ScrollArea>
          <AmoriContainer isOpen={isAmoriOpen} setOpenChange={setIsAmoriOpen} />
        </>
      ) : (
        /* Desktop: full-width single canvas. The Amori chat lives in the
           global ChatBot widget (floating button + compact panel). */
        <div className="bg-white min-h-screen -mt-32 pt-28">
          <DesktopLayout className="pt-6">{mainContent}</DesktopLayout>
        </div>
      )}
    </>
  );
}
